import React, { useState } from 'react';
import { useBlog } from '../../context/BlogContext';

export default function PostSettingsPanel({ post, onChange, collapsed: initialCollapsed }) {
  const { categories } = useBlog();
  const [collapsed, setCollapsed] = useState(!!initialCollapsed);

  const handleChange = (field, value) => {
    onChange({ ...post, [field]: value });
  };

  const handleTagsChange = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const tag = e.target.value.trim().toLowerCase();
      if (!post.tags.includes(tag)) {
        handleChange('tags', [...post.tags, tag]);
      }
      e.target.value = '';
    }
  };

  const removeTag = (tag) => {
    handleChange('tags', post.tags.filter(t => t !== tag));
  };

  const handleHeroChange = (field, value) => {
    handleChange('heroSlider', { ...post.heroSlider, [field]: value });
  };

  const addHeroSlide = () => {
    handleChange('heroSlider', {
      ...post.heroSlider,
      slides: [...(post.heroSlider.slides || []), { image: '', alt: '' }],
    });
  };

  const updateHeroSlide = (index, field, value) => {
    const slides = [...post.heroSlider.slides];
    slides[index] = { ...slides[index], [field]: value };
    handleChange('heroSlider', { ...post.heroSlider, slides });
  };

  const removeHeroSlide = (index) => {
    handleChange('heroSlider', {
      ...post.heroSlider,
      slides: post.heroSlider.slides.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="adminblog-settings-panel">
      {/* Collapsible header */}
      <div
        className="adminblog-panel-card"
        style={{ cursor: initialCollapsed ? 'pointer' : 'default' }}
        onClick={initialCollapsed ? () => setCollapsed(!collapsed) : undefined}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Post Settings</h4>
          {initialCollapsed && (
            <span style={{ fontSize: 12, color: '#999' }}>{collapsed ? '▸ Show' : '▾ Hide'}</span>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
      {/* Publish Settings */}
      <div className="adminblog-panel-card">
        <h4>Publish</h4>
        <div className="adminblog-field">
          <label>Status</label>
          <div className="adminblog-status-toggle">
            <button
              className={`adminblog-status-option ${post.status === 'draft' ? 'active' : ''}`}
              onClick={() => handleChange('status', 'draft')}
            >
              Draft
            </button>
            <button
              className={`adminblog-status-option ${post.status === 'published' ? 'active' : ''}`}
              onClick={() => handleChange('status', 'published')}
            >
              Published
            </button>
          </div>
        </div>
        <div className="adminblog-field">
          <label>Title</label>
          <input
            type="text"
            value={post.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Post title"
          />
        </div>
        <div className="adminblog-field">
          <label>Slug</label>
          <input
            type="text"
            value={post.slug}
            onChange={(e) => handleChange('slug', e.target.value)}
            placeholder="post-slug"
          />
          <p className="adminblog-field-hint">/journal/{post.slug}</p>
        </div>
        <div className="adminblog-field">
          <label>Excerpt</label>
          <textarea
            value={post.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            placeholder="Short description for cards..."
            rows={3}
          />
        </div>
      </div>

      {/* Layout */}
      <div className="adminblog-panel-card">
        <h4>Layout</h4>
        <div className="adminblog-field">
          <label>Ancho del contenido</label>
          <select
            value={post.contentWidth || 'narrow'}
            onChange={(e) => handleChange('contentWidth', e.target.value)}
          >
            <option value="narrow">Angosto (800px)</option>
            <option value="wide">Ancho (1120px)</option>
            <option value="x1280">Amplio (1280px)</option>
            <option value="x1480">Extra amplio (1480px)</option>
            <option value="full">Completo</option>
          </select>
          <p className="adminblog-field-hint">Controla el ancho máximo del cuerpo del artículo.</p>
        </div>
      </div>

      {/* Category & Tags */}
      <div className="adminblog-panel-card">
        <h4>Taxonomy</h4>
        <div className="adminblog-field">
          <label>Category</label>
          <select
            value={post.category}
            onChange={(e) => handleChange('category', e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div className="adminblog-field">
          <label>Tags</label>
          <input
            type="text"
            placeholder="Press Enter to add tag"
            onKeyDown={handleTagsChange}
          />
          {post.tags.length > 0 && (
            <div className="adminblog-tags-display">
              {post.tags.map(tag => (
                <span key={tag} className="adminblog-tag">
                  {tag}
                  <span className="adminblog-tag-remove" onClick={() => removeTag(tag)}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail */}
      <div className="adminblog-panel-card">
        <h4>Thumbnail</h4>
        <div className="adminblog-field">
          <label>Image URL</label>
          <input
            type="text"
            value={post.thumbnail}
            onChange={(e) => handleChange('thumbnail', e.target.value)}
            placeholder="https://..."
          />
          {post.thumbnail && (
            <img src={post.thumbnail} alt="Thumbnail" className="adminblog-thumb-preview" />
          )}
        </div>
      </div>

      {/* Hero Slider */}
      <div className="adminblog-panel-card">
        <h4>Hero Slider</h4>
        <div className="adminblog-field">
          <label>
            <input
              type="checkbox"
              checked={post.heroSlider?.enabled || false}
              onChange={(e) => handleHeroChange('enabled', e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Enable hero slider
          </label>
        </div>
        {post.heroSlider?.enabled && (
          <>
            <div className="adminblog-field">
              <label>
                <input
                  type="checkbox"
                  checked={post.heroSlider?.autoplay || false}
                  onChange={(e) => handleHeroChange('autoplay', e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Autoplay
              </label>
            </div>
            {post.heroSlider?.autoplay && (
              <div className="adminblog-field">
                <label>Delay (ms)</label>
                <input
                  type="number"
                  value={post.heroSlider?.autoplayDelay || 5000}
                  onChange={(e) => handleHeroChange('autoplayDelay', parseInt(e.target.value) || 5000)}
                />
              </div>
            )}
            <div className="adminblog-hero-slides">
              {(post.heroSlider?.slides || []).map((slide, i) => (
                <div key={i} className="adminblog-list-item">
                  <input
                    type="text"
                    value={slide.image}
                    onChange={(e) => updateHeroSlide(i, 'image', e.target.value)}
                    placeholder="Image URL"
                  />
                  <input
                    type="text"
                    value={slide.alt}
                    onChange={(e) => updateHeroSlide(i, 'alt', e.target.value)}
                    placeholder="Alt text"
                    style={{ maxWidth: 120 }}
                  />
                  <button className="adminblog-list-remove" onClick={() => removeHeroSlide(i)}>×</button>
                </div>
              ))}
              <button className="adminblog-list-add" onClick={addHeroSlide}>+ Add Slide</button>
            </div>
          </>
        )}
      </div>
        </>
      )}
    </div>
  );
}
